// src/components/Navbar.jsx

import { useCartItemCount } from '@/hooks/useShoppingCart';
import { Loader2, LogOut, ShoppingCart } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

/**
 * Navigation bar component
 * @param {Object} props - Component props
 * @returns {JSX.Element} Navbar component
 */
const StoreNavbar = ({ user, handleLogout }) => {
  const location = useLocation();
  /**
   * Check if current path is active
   * @param {string} path - Path to check
   * @returns {boolean} True if path is active
   */
  const isActive = (path) => {
    return location.pathname === path;
  };

  const { data: cartItemsCount, isLoading: isCartItemsCountLoading } =
    useCartItemCount();

  return (
    <nav className="bg-primary shadow-sm border-b border-gray-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-auto py-4">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center space-x-3">
            <img src="/logo.svg" alt="Patriot Claims" className="h-16 w-auto" />
          </Link>

          {/* Navigation Links */}
          {/* <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/store/products"
              className={`text-sm font-medium transition-colors duration-200 ${
                isActive('/store/products') || isActive('/store')
                  ? 'text-white border-b-2 border-white pb-1'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              Products
            </Link>
          </div> */}

          {/* Right Side Actions */}
          <div className="flex items-center space-x-4">
            {/* Cart */}
            {isCartItemsCountLoading ? (
              <Loader2 className="h-6 w-6 text-secondary animate-spin" />
            ) : (
              <Link
                to="/store/cart"
                className="relative p-2 text-gray-300 hover:text-white transition-colors duration-200"
              >
                <ShoppingCart className="h-6 w-6" />
                {cartItemsCount?.item_count > 0 && (
                  <span className="absolute -top-0 -right-1 bg-secondary text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {cartItemsCount?.item_count}
                  </span>
                )}
              </Link>
            )}

            {/* User Menu */}
            <div className="flex items-center space-x-4">
              <div className="h-8 w-8 bg-gray-600 rounded-full flex items-center justify-center overflow-hidden">
                {user?.photoURL ? (
                  <img
                    src={user.photoURL}
                    alt="Profile"
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null; // Prevent infinite loop
                      e.target.src =
                        'https://static.vecteezy.com/system/resources/thumbnails/002/318/271/small/user-profile-icon-free-vector.jpg';
                    }}
                  />
                ) : (
                  <svg
                    className="h-5 w-5 text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                )}
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-gray-300 hover:text-white transition-colors duration-200"
              >
                <span className="text-sm font-medium hidden sm:block">
                  Sign Out
                </span>
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className="md:hidden border-t border-gray-700">
        <div className="px-4 py-2">
          <Link
            to="/store/products"
            className={`block px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
              isActive('/store/products') || isActive('/store')
                ? 'text-white bg-gray-700'
                : 'text-gray-300 hover:text-white hover:bg-gray-700'
            }`}
          >
            Products
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default StoreNavbar;
